import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Smartphone } from 'lucide-react';
import { useI18n } from '../i18n';
import { capabilities, addSnapShortcut, addQuickTile } from '../lib/nativePhone';

// Capability discovery is read-only. Only a deliberate tap opens a system request.
export default function SnapShortcutControls({ prefix = 'snap', camera = false, includeTile = false }) {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [caps, setCaps] = useState(null);
    const [busy, setBusy] = useState(null);
    const [status, setStatus] = useState(null);
    const alive = useRef(true);
    const inFlight = useRef(false);

    useEffect(() => {
        alive.current = true;
        let active = true;
        capabilities().then(result => { if (active) setCaps(result || {}); });
        return () => { active = false; alive.current = false; };
    }, []);

    const request = async kind => {
        if (inFlight.current || !(kind === 'tile' ? caps?.tilePrompt : caps?.shortcutPin)) return;
        inFlight.current = true;
        setBusy(kind);
        setStatus(null);
        const result = await (kind === 'tile' ? addQuickTile() : addSnapShortcut());
        if (alive.current) {
            setStatus(kind === 'tile'
                ? (result.ok ? 'tile_requested' : 'failed')
                : (result.ok && result.requested ? 'requested' : 'failed'));
            setBusy(null);
        }
        inFlight.current = false;
    };
    const buttonStyle = camera
        ? 'min-h-[44px] rounded-full px-3 py-2 border border-white/30 bg-black/60 text-white text-xs disabled:opacity-60'
        : 'min-h-[44px] rounded-full px-3 py-2 border border-brand-border text-xs';

    return <div className={camera ? 'flex flex-wrap items-center gap-2' : 'dashboard-find-shortcut'} data-testid={`${prefix}-shortcuts`}>
        {caps?.shortcutPin === true && <button type="button" data-testid={`${prefix}-pin-snap`} className={buttonStyle}
            onClick={() => request('pin')} disabled={!!busy} aria-busy={busy === 'pin'}>
            <Smartphone size={17} aria-hidden="true" className="inline-block mr-1" />
            {t(busy === 'pin' ? 'common.loading' : 'phone.snap_shortcut_add')}
        </button>}
        {includeTile && caps?.tilePrompt === true && <button type="button" data-testid={`${prefix}-snap-tile`} className={buttonStyle}
            onClick={() => request('tile')} disabled={!!busy} aria-busy={busy === 'tile'}>
            <LayoutGrid size={17} aria-hidden="true" className="inline-block mr-1" />{t('phone.snap_tile_add')}
        </button>}
        {(!caps?.shortcutPin || status === 'failed') && <button type="button" data-testid={`${prefix}-snap-shortcut-help`}
            className={buttonStyle} onClick={() => navigate('/phone-theme')}>
            <Smartphone size={17} aria-hidden="true" className="inline-block mr-1" />{t('phone.snap_shortcut_help')}
        </button>}
        {camera && <p className="w-full text-[11px] text-white/80">{t('phone.snap_unlock_note')}</p>}
        {status && <p role="status" className={camera ? 'w-full text-xs text-white' : ''}
            data-testid={`${prefix}-shortcut-status`}>{t(status === 'tile_requested' ? 'phone.snap_tile_requested' : `phone.snap_shortcut_${status}`)}</p>}
    </div>;
}
